import React, { FC, useEffect, useCallback, useRef } from 'react';
import GSApi from 'api/Api';
import PlaybackManager from 'providers/player/PlaybackManager';
import { ControlGroup, PlayerWrapper } from 'components/Player/PlayerWrapper';
import { LivePlayerAssetInfo, PlayerEvents, PlayingAssetType, PlayingMode } from 'types/Player';
import { usePlayer } from 'providers/player/PlayerContext';
import { useDataFetcher } from 'hooks/useDataFetcher/useDataFetcher';
import { useLiveProgramSync } from 'hooks/useLiveProgramSync/useLiveProgramSync';
import { MiniEpg } from 'components/MiniEpg/MiniEpg';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { useAuth } from 'providers/useAuth/AuthContext';
import { FetchPlayingAsset, StreamIDKey } from 'types/ApiTypes';
import { useGlobalNetworkError, useGlobalNoInternet, withNetworkCheck } from 'hooks/withNetworkCheck/withNetworkCheck';
import { HelmetWrapper } from 'components/HelmetWrapper/HelmetWrapper';
import { getPlayerPageHelmetData } from 'utils/fnHelmet';
import { getErrorStatus } from 'utils/fnError';
import { withPlayer } from 'hooks/withPlayer/withPlayer';
import { useRecording } from 'providers/useRecording/RecordingContext';
import { RecordingEvent } from 'types/RecordingTypes';
import { useChannels } from 'providers/useChannels/useChannels';
import { useNavigation } from 'hooks/useNavigation';
import CastHandler from 'providers/cast/CastHandler';
import { useCast } from 'providers/cast/CastContext';
import { savePageInfo } from 'utils/pageInfoStorage';
import PageTakeover from 'components/Pages/Settings/PageTakeover/PageTakeover';

export const LivePlayerPage: FC<{ channelId: string; isEmbedded: boolean }> = ({ channelId, isEmbedded }) => {
    const { isGuest, openGuestAlert } = useAuth();
    const { castConnected } = useCast();

    const { config } = useConfig();

    const channelProductCodeRef = useRef<string>(null);

    const { onNoInternet, noInternet } = useGlobalNoInternet();
    const { onNetworkError, networkError } = useGlobalNetworkError();
    const { addEventListener: addRecordingEventListener, removeEventListener: removeRecordingEventListener } = useRecording();

    const { response, error, loading, fetcher, reset } = useDataFetcher<any, FetchPlayingAsset>(
        () => GSApi.createStreamSession(PlayingAssetType.LIVE, StreamIDKey.CHANNEL_ID, channelId, {}, (res) => res),
        onNoInternet,
        onNetworkError
    );

    const sessionCreated = useRef<boolean>(false);
    const reFetchTimeoutRef = useRef(null);
    const previousChannelId = useRef<string | null>(null);
    const onStreamEndReachedRef = useRef<() => void>(null);
    const hadNetworkIssueRef = useRef<boolean>(false);

    const { channels, fetchChannels } = useChannels();

    const { currentProgram, programTiming, programDetails, syncProgram } = useLiveProgramSync(channelId);

    const {
        setAsset,
        asset: currentPlayerAsset,
        addEventListener,
        removeEventListener,
        resetPlayerAsset,
        setActiveControlGroup,
        playingMode,
        activeControlGroup,
        isMini,
        closeMiniPlayer,
        engine,
    } = usePlayer();
    const { onBackPressed } = useNavigation();

    const fetchDetails = () => {
        if (!isGuest) {
            fetcher(
                {
                    id: channelId,
                    createSession: !sessionCreated.current,
                },
                false
            );
        }
    };

    const onStreamEndReached = () => {
        if (CastHandler.isCast()) {
            return;
        }

        if (PlaybackManager.getPlaybackMode() === PlayingMode.TIME_SHIFT) {
            if (isMini) {
                closeMiniPlayer();
            } else {
                onBackPressed();
            }
            return;
        }

        sessionCreated.current = false;
        resetPlayerAsset();
        fetchDetails();
    };

    const onProgramEnd = () => syncProgram();

    const onJumpToLiveRequested = useCallback(async () => {
        await PlaybackManager.switchToNormalMode();
    }, []);

    const setRecorded = () => {
        setAsset({
            ...currentPlayerAsset,
            isRecorded: true,
        });
    };

    const setNotRecorded = () => {
        setAsset({
            ...currentPlayerAsset,
            isRecorded: false,
        });
    };

    useEffect(() => {
        if (isGuest) {
            openGuestAlert('/');
        }

        return () => {
            if (reFetchTimeoutRef.current) {
                clearTimeout(reFetchTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        // When casting, a cast session is already active for the receiver — returning to the player (ex: from the cast mini-controller) is not gonna do a new POST stream session.
        // But a channel switch while casting MUST create a new session so the receiver loads the newly selected channel.
        resetPlayerAsset(previousChannelId.current == null ? CastHandler.getPlayMode() : undefined);

        const isChannelSwitch = previousChannelId.current != null && previousChannelId.current !== channelId;

        if (CastHandler.isCast() && !isChannelSwitch) {
            const castAsset = PlaybackManager.getPlayingAsset();
            if (castAsset) {
                sessionCreated.current = true;
                setAsset(castAsset);
            }
        } else {
            sessionCreated.current = false;
            fetchDetails();
        }

        setActiveControlGroup(ControlGroup.BASE);
        addEventListener(PlayerEvents.JUMP_TO_LIVE, onJumpToLiveRequested);
        document.addEventListener(PlayerEvents.END_OF_PROGRAM, onProgramEnd);

        previousChannelId.current = channelId;

        return () => {
            removeEventListener(PlayerEvents.JUMP_TO_LIVE, onJumpToLiveRequested);
            document.removeEventListener(PlayerEvents.END_OF_PROGRAM, onProgramEnd);
        };
    }, [channelId, onJumpToLiveRequested]);

    useEffect(() => {
        onStreamEndReachedRef.current = onStreamEndReached;
    });

    useEffect(() => {
        const hasNetworkIssue = noInternet || !!networkError;

        if (hadNetworkIssueRef.current && !hasNetworkIssue) {
            engine.current?.recoverStream();
        }

        hadNetworkIssueRef.current = hasNetworkIssue;
    }, [noInternet, networkError]);

    useEffect(() => {
        const handler = () => onStreamEndReachedRef.current();

        addEventListener(PlayerEvents.END_OF_STREAM, handler);

        return () => {
            removeEventListener(PlayerEvents.END_OF_STREAM, handler);
        };
    }, [channelId]);

    useEffect(() => {
        if (!response?.stream_url && getErrorStatus(error) !== 500) {
            return;
        }

        const currentChannel = channels?.find((obj) => (obj.id === channelId ? obj : null));

        if (!currentChannel) {
            return;
        }

        if (currentProgram && currentProgram.channelId !== channelId) {
            return;
        }

        // Time shifted playback runs on its own replay session
        if (playingMode === PlayingMode.TIME_SHIFT) {
            return;
        }

        const LiveAssetProps = {
            type: 'LIVE' as const,
            use_case: PlayingAssetType.LIVE,
            ...programTiming,
            channelName: currentChannel?.name,
            channelLogo: currentChannel?.logo,
            channelHasReplay: currentChannel?.replay,
            channelSubscribed: currentChannel?.subscription,
        };

        const assetToSet: LivePlayerAssetInfo =
            getErrorStatus(error) === 500
                ? LiveAssetProps
                : {
                      ...response,
                      ...LiveAssetProps,
                      ...programDetails,
                  };

        PlaybackManager.adoptSession(assetToSet.id, response?.heartbeat_interval);

        if (!sessionCreated.current) {
            sessionCreated.current = true;
        }

        setAsset(assetToSet);

        if (!error) {
            channelProductCodeRef.current = response?.channelProductCode;
        }
    }, [response, programTiming, programDetails, error, channels]);

    useEffect(() => {
        if (response && playingMode) {
            if (playingMode === PlayingMode.TIME_SHIFT) {
                clearTimeout(reFetchTimeoutRef.current);
            } else if (response.endTime) {
                clearTimeout(reFetchTimeoutRef.current);
                reFetchTimeoutRef.current = setTimeout(fetchDetails, response.endTime - Date.now());
            }
            savePageInfo('LIVE Player');
        }
    }, [response, playingMode]);

    useEffect(() => {
        addRecordingEventListener(RecordingEvent.ADD, setRecorded);
        addRecordingEventListener(RecordingEvent.DELETE, setNotRecorded);

        return () => {
            removeRecordingEventListener(RecordingEvent.ADD, setRecorded);
            removeRecordingEventListener(RecordingEvent.DELETE, setNotRecorded);
        };
    }, [channelId, currentPlayerAsset]);

    useEffect(() => {
        fetchChannels();
        return () => {
            previousChannelId.current = null;
        };
    }, []);

    if (error) {
        if (getErrorStatus(error) === 409) {
            return (
                <PageTakeover
                    limitReached
                    onSessionDeleted={() => {
                        reset();
                        fetchDetails();
                    }}
                />
            );
        }
    }

    return (
        <>
            {response && !isEmbedded && <HelmetWrapper {...getPlayerPageHelmetData(currentProgram)} />}
            <PlayerWrapper
                loading={loading}
                extraControls={
                    !isEmbedded && !castConnected && config.isFeatureActive('mini_epg') ? (
                        <MiniEpg currentChannel={channelId} activeGroup={activeControlGroup} />
                    ) : null
                }
            />
        </>
    );
};

export default withNetworkCheck(withPlayer(LivePlayerPage), { keepMounted: () => !!PlaybackManager.isPlayerLoaded() });
